import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { CurrencyInput } from 'components/ui/currency-input';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Typography } from 'components/ui/typography';

import { useSetupDrawdown } from '../hooks/useBilling';

type Props = {
  onSuccess: () => void;
  onSkip?: () => void;
  submitLabel?: string;
};

export function DrawdownSetupForm({
  onSuccess,
  onSkip,
  submitLabel = 'Enable Drawdown Billing',
}: Props) {
  const [targetDollars, setTargetDollars] = useState('');
  const [minDollars, setMinDollars] = useState('');
  const [chargeAutomatically, setChargeAutomatically] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { setupDrawdown } = useSetupDrawdown();

  const validateInputs = (): string | null => {
    const target = parseFloat(targetDollars);
    const min = parseFloat(minDollars);

    if (!targetDollars || isNaN(target)) {
      return 'Target balance is required';
    }

    if (!minDollars || isNaN(min)) {
      return 'Minimum threshold is required';
    }

    if (target < 0) {
      return 'Target balance must be non-negative';
    }

    if (min < 0) {
      return 'Minimum threshold must be non-negative';
    }

    if (min > target) {
      return 'Minimum threshold must be less than or equal to target balance';
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    const target = parseFloat(targetDollars);
    const min = parseFloat(minDollars);

    setIsSubmitting(true);
    try {
      await setupDrawdown({
        targetBalanceSubunits: Math.round(target * 100),
        minBalanceSubunits: Math.round(min * 100),
        chargeAutomatically,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable drawdown billing');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="target-balance">Target Balance</Label>
          <Typography variant="small" as="p">
            The amount your account will be funded to, minus any processing fees.
          </Typography>
          <CurrencyInput
            id="target-balance"
            value={targetDollars}
            onChange={setTargetDollars}
            placeholder="10,000.00"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="min-threshold">Minimum Threshold</Label>
          <Typography variant="small" as="p">
            When your balance drops below this amount, we'll invoice you to bring it back to the
            target.
          </Typography>
          <CurrencyInput
            id="min-threshold"
            value={minDollars}
            onChange={setMinDollars}
            placeholder="5,000.00"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Input
            id="currency"
            type="text"
            value="USD — US Dollar"
            disabled
            className="text-muted-foreground"
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="charge-automatically"
            checked={chargeAutomatically}
            onCheckedChange={(checked) => setChargeAutomatically(checked === true)}
            disabled={isSubmitting}
          />
          <Label htmlFor="charge-automatically">Automatically pay invoices</Label>
        </div>
      </div>

      {error && (
        <Typography variant="small" as="p" className="text-destructive">
          {error}
        </Typography>
      )}

      <div className="flex items-center gap-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Enabling...' : submitLabel}
        </Button>
        {onSkip && (
          <Button type="button" variant="ghost" onClick={onSkip} disabled={isSubmitting}>
            Skip for now
          </Button>
        )}
      </div>

      <AlertDialog
        open={showConfirm}
        onOpenChange={(open) => !isSubmitting && setShowConfirm(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Drawdown Billing?</AlertDialogTitle>
            <AlertDialogDescription>
              {isSubmitting ? (
                'Setting up your account, this may take a moment...'
              ) : (
                <>
                  Your account will be funded{' '}
                  <strong className="text-foreground">
                    $
                    {parseFloat(targetDollars).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                    })}
                  </strong>
                  . When your balance drops below{' '}
                  <strong className="text-foreground">
                    ${parseFloat(minDollars).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </strong>
                  ,{' '}
                  {chargeAutomatically
                    ? 'your payment method will be charged automatically.'
                    : "you'll receive an invoice when a top-up is needed."}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {!isSubmitting && <AlertDialogCancel>Cancel</AlertDialogCancel>}
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enabling...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
